import actualizarImg from '@assets/actualizar.png'
import comfortPreviewConfort from '@assets/atemporal/_confort.png'
import comfortPreviewPlus from '@assets/atemporal/_plus.png'
import comfortPreviewRegular from '@assets/atemporal/_regular.png'
import comfortPreviewSleep from '@assets/atemporal/_sleep.png'
import seatHeightPreview42 from '@assets/atemporal/Altura 42.jpg'
import seatHeightPreview47 from '@assets/atemporal/Altura 47.jpg'
import armPreviewB1 from '@assets/atemporal/B1.jpg'
import armPreviewB2 from '@assets/atemporal/B2.jpg'
import armPreviewB3 from '@assets/atemporal/B3.jpg'
import armPreviewB4 from '@assets/atemporal/B4.jpg'
import armPreviewB5 from '@assets/atemporal/B5.jpg'
import seatStitchConPreview from '@assets/atemporal/Con costura.png'
import cushionPreviewMulti from '@assets/atemporal/multi.jpg'
import cushionPreviewRectangular from '@assets/atemporal/Rectangular.jpg'
import seatStitchSinPreview from '@assets/atemporal/Sin costuras.png'
import { useArmRestrictionNotice } from '@components/ArmsCushionsPanel/ArmRestrictionNoticeModal'
import type {
  ArmsCushionsPanelProps,
  ArmsCushionsPanelView,
  SeatHeightOption,
  SeatStitchOption,
} from '@components/ArmsCushionsPanel/types'
import { Submenu } from '@hooks/useFetchMenu'
import { getProxyUrl } from '@proxies/index'
import * as Tooltip from '@radix-ui/react-tooltip'
import { useAppStore } from '@store'
import { AnyID } from '@types'
import {
  buildRelaxArmVariantCatalog,
  collectRelaxBaseCodes,
  findRelaxArmVariantInMenu,
  findRelaxBaseUnitInMenu,
  getRelaxCatalogFromSceneItem,
  isRelaxArmVariantMenuItem,
  isRelaxIntegratedSceneItem,
  parseRelaxArmButtonCode,
  parseRelaxArmVariantCatalog,
} from '@utils/atemporal/relaxArmVariants'
import {
  buildCatalogWithSeatHeightCm,
  childBaseCatalogFromUnit,
  isBaseSeatParentSceneItem,
  parseSeatHeightFromCatalog,
  resolveSceneSeatHeightCmForDisplay,
  seatHeightOptionUnavailableMessage,
  seatHeightVariantExistsInCatalog,
} from '@utils/atemporal/seatHeightCatalog'
import {
  COMFORT_OPTIONS,
  ComfortOption,
  buildCatalogWithDepthCm,
  comfortOptionUnavailableMessage,
  comfortVariantExistsInCatalog,
  getComfortOptionFromCatalog,
  isArmCatalog,
  isComfortVariantEligible,
} from '@utils/atemporal/unitDepthVariants'
import { withCameraFitBlocked } from '@utils/camera/withCameraFitBlocked'
import { runWithHistoryBatchAsync } from '@utils/historyRecording'
import { getArmSideSign as getArmSideSignFromPlacement } from '@utils/linkedArms/armPlacement'
import { createArmRowContext, getLinkedModulesInRow } from '@utils/linkedArms/armRowContext'
import {
  RELAX_ARM_NOTICE,
  resolveConstrainedRelaxArmVariant,
  type RelaxSideBlockState,
} from '@utils/linkedArms/relaxArmSideAvailability'
import {
  ARM_NOTICE_ONLY_ONE_SIDE,
  unitAllowsOnlyOneArm,
  unitArmHandednessLetter,
} from '@utils/linkedArms/singleArmSeriesRules'
import getId from '@utils/sceneObjects/getId'
import { isRinconeroCatalog } from '@utils/sceneObjects/items/rinconeroUtils'
import { ui } from '@utils/i18n/uiCopy'
import { motion } from 'framer-motion'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Matrix4, Quaternion, Vector3 } from 'three'

const GLOBAL_CUSHION_CATALOG_KEY = 'atemporal:autoGroup:cushionCatalog'
const GLOBAL_SEAT_HEIGHT_KEY = 'atemporal:autoGroup:seatHeightCm'
const GLOBAL_SEAT_STITCH_KEY = 'atemporal:autoGroup:seatStitchType'
const TEMP_ARM_PLACEHOLDER_IMAGE =
  'https://atemporalinternalapi.for2home.com/CatalogData/ItemImages/MainMenu/nada.png'

const COMFORT_PREVIEW_SRC: Record<ComfortOption, string> = {
  regular: comfortPreviewRegular,
  comfort: comfortPreviewConfort,
  plus: comfortPreviewPlus,
  sleep: comfortPreviewSleep,
}

const ARMS_CODES = ['BR1', 'BR2', 'BR3', 'BR4', 'BR5'] as const

const ARM_PREVIEW_SRC: Record<(typeof ARMS_CODES)[number], string> = {
  BR1: armPreviewB1,
  BR2: armPreviewB2,
  BR3: armPreviewB3,
  BR4: armPreviewB4,
  BR5: armPreviewB5,
}

function getArmPreviewSrc(claveMenuItem: string | undefined): string | undefined {
  if (!claveMenuItem) return undefined
  const m = String(claveMenuItem)
    .trim()
    .toUpperCase()
    .match(/^BR([1-5])$/)
  if (!m) return undefined
  return ARM_PREVIEW_SRC[`BR${m[1]}` as (typeof ARMS_CODES)[number]]
}

/** Misma caja vertical que brazos (más alta que ancha); no usar en cojín/asiento/altura */
const PORTRAIT_OPTION_TILE_CLASS =
  'relative flex h-[5.5rem] w-[3.75rem] shrink-0 cursor-pointer items-center justify-center overflow-visible rounded border-2 transition-all duration-200 min-[640px]:h-[6rem] min-[640px]:w-20 laptop:h-[6.5rem] laptop:w-20 min-[2400px]:h-[13rem] min-[2400px]:w-40'

/** Tiles cuadrados: altura asiento, costura asiento y tipo cojín (imágenes un poco más grandes que w-20) */
const SQUARE_PREVIEW_TILE_CLASS =
  'relative aspect-square w-[5rem] shrink-0 cursor-pointer rounded border-2 transition-all duration-200 min-[640px]:w-24 laptop:w-24 min-[2400px]:w-52'

const CUSHION_CODES = [
  { codigo: 'Cojines 6-95', claveMenuItem: '3COJINES60X602COJINES50X50' },
  { codigo: 'CojinesR 6-95', claveMenuItem: '2COJINESRESPALDO' },
] as const
const DEFAULT_CUSHION_IMAGE =
  'https://atemporalinternalapi.for2home.com/CatalogData/ItemImages/MainMenu/nada.png'

/** Miniaturas fijas cojines (assets/atemporal); misma regla que getCushionSubtitle */
function getCushionPreviewSrc(item: Submenu): string | undefined {
  if (item.id === 14 || item.claveMenuItem === '2COJINESRESPALDO') {
    return cushionPreviewRectangular
  }
  if (item.id === 15 || item.claveMenuItem === '3COJINES60X602COJINES50X50') {
    return cushionPreviewMulti
  }
  const u = String(item.claveMenuItem || '').toUpperCase()
  if (u === '2COJINESRESPALDO') return cushionPreviewRectangular
  if (u === '3COJINES60X602COJINES50X50') return cushionPreviewMulti
  return undefined
}

/**
 * Componente unificado Brazos y Cojines - Toggle entre mostrar brazos o cojines
 * Reemplaza los componentes separados ArmsPanel y CushionsPanel
 */
export function ArmsCushionsPanel({
  menuData,
  relaxMenuData,
  presentation = 'docked',
}: ArmsCushionsPanelProps) {
  const [activeView, setActiveView] = useState<ArmsCushionsPanelView>('comfort')
  const [selectedComfort, setSelectedComfort] = useState<ComfortOption | null>(null)
  const [selectedCushion, setSelectedCushion] = useState<string | null>(null)
  const [selectedSeatHeightCm, setSelectedSeatHeightCm] = useState<SeatHeightOption | null>(null)
  const [selectedSeatStitch, setSelectedSeatStitch] = useState<SeatStitchOption | null>(null)
  const [apiArmsItems, setApiArmsItems] = useState<Submenu[]>([])
  const [apiCushionsItems, setApiCushionsItems] = useState<Submenu[]>([])

  const drawItem = useAppStore((s) => s.drawItem)
  const setCollision = useAppStore((s) => s.setCollision)
  const setCurrentObjectID = useAppStore((s) => s.setCurrentObjectID)
  const selectedItems = useAppStore(
    (store) => store.layers[store.currentLayer].selected,
  ) as Set<any>
  const items = useAppStore((s) => s.items)
  const currentObjectID = useAppStore((s) => s.currentObjectID)
  const replaceItem = useAppStore((s) => s.replaceItem)
  const editItem = useAppStore((s) => s.editItem)
  const editZone = useAppStore((s) => s.editZone)
  const collideItems = useAppStore((s) => s.collideItems)
  const setFrameloop = useAppStore.use.setFrameloop()
  const getMenuItemCatalog = (item: Submenu) =>
    item.args && item.args.length > 0 ? item.args[0] : ''

  const relaxArmVariants = useMemo(
    () => (relaxMenuData?.submenus ?? []).filter(isRelaxArmVariantMenuItem),
    [relaxMenuData?.submenus],
  )
  const relaxBaseCodes = useMemo(
    () => collectRelaxBaseCodes(relaxMenuData?.submenus ?? []),
    [relaxMenuData?.submenus],
  )

  // Protección contra doble ejecución
  const isProcessingArm = useRef(false)
  const isProcessingComfort = useRef(false)
  const isProcessingSeatHeight = useRef(false)
  const frameloopTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const showArmRestrictionNotice = useArmRestrictionNotice((s) => s.show)

  useEffect(() => {
    return () => {
      if (frameloopTimeoutRef.current) {
        clearTimeout(frameloopTimeoutRef.current)
        frameloopTimeoutRef.current = undefined
      }
    }
  }, [])

  const buildSubmenuFromUnidad = (
    rawUnidad: any,
    code: string,
    forcedClaveMenuItem?: string,
  ): Submenu => {
    const normalizedCatalog =
      String(rawUnidad?.catalogID || rawUnidad?.codigoUnidad || code).trim() || code
    const normalizedClave =
      forcedClaveMenuItem || String(rawUnidad?.claveMenuItem || normalizedCatalog).trim()
    return {
      accion: null,
      alto: null,
      altoicono: null,
      ancho: null,
      anchoicono: null,
      // Nota: el backend histórico usa args como array en runtime.
      args: [normalizedCatalog] as any,
      claveMenuItem: normalizedClave,
      clavemenuabrir: '',
      codigoUnidad: normalizedCatalog,
      descripcion: rawUnidad?.descripcion || rawUnidad?.texto || code,
      descripcionLarga: rawUnidad?.descripcionLarga || null,
      fondo: null,
      icono: String(rawUnidad?.image || rawUnidad?.icono || rawUnidad?.miniatura || ''),
      id: Number(rawUnidad?.id || rawUnidad?.idUnidades || Math.random()),
      idUnidades: Number(rawUnidad?.idUnidades || 0) || null,
      morphs: null,
      orden: Number(rawUnidad?.orden || 0),
      referencia: rawUnidad?.referencia || null,
      texto: String(rawUnidad?.texto || rawUnidad?.descripcion || code),
    }
  }

  useEffect(() => {
    let cancelled = false

    const fetchUnidadByCodigo = async (codigo: string) => {
      const token =
        typeof window !== 'undefined'
          ? window.sessionStorage.getItem('access_token')
          : sessionStorage.getItem('access_token')
      if (!token) return null

      const response = await fetch(getProxyUrl('api/Unidades/getunidad'), {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept-Language': typeof navigator !== 'undefined' && !String(navigator.language||'').toLowerCase().startsWith('es') ? 'en' : 'es',
          'Content-Type': 'application/json',
          Authorization: `Ninpo ${token}`,
        },
        body: JSON.stringify({
          codigo,
          date: new Date().toISOString(),
        }),
      })
      if (!response.ok) return null
      const json = await response.json()
      if (Array.isArray(json)) return json[0] || null
      return json || null
    }

    const loadArmsAndCushions = async () => {
      try {
        const [armsResult, cushionsResult] = await Promise.all([
          Promise.all(ARMS_CODES.map((code) => fetchUnidadByCodigo(code))),
          Promise.all(CUSHION_CODES.map((entry) => fetchUnidadByCodigo(entry.codigo))),
        ])

        if (cancelled) return

        const nextArms = armsResult
          .map((raw, i) => (raw ? buildSubmenuFromUnidad(raw, ARMS_CODES[i], ARMS_CODES[i]) : null))
          .filter(Boolean) as Submenu[]
        const nextCushions = cushionsResult
          .map((raw, i) =>
            raw
              ? {
                  ...buildSubmenuFromUnidad(
                    raw,
                    CUSHION_CODES[i].codigo,
                    CUSHION_CODES[i].claveMenuItem,
                  ),
                  icono: DEFAULT_CUSHION_IMAGE,
                }
              : null,
          )
          .filter(Boolean) as Submenu[]

        if (nextArms.length > 0) setApiArmsItems(nextArms)
        if (nextCushions.length > 0) setApiCushionsItems(nextCushions)
      } catch (error) {
        console.warn('[ArmsCushionsPanel] Error cargando unidades de API getunidad:', error)
      }
    }

    loadArmsAndCushions()
    return () => {
      cancelled = true
    }
  }, [])

  // Filtrar items para brazos y cojines (API primero, fallback al menú actual)
  const fallbackArmsItems =
    menuData?.submenus?.filter((item: any) => item.claveMenuItem?.startsWith('BR')) || []
  const isSpecialCushionItem = (item: Submenu) => {
    const key = String(item?.claveMenuItem || '').toUpperCase()
    const text = String(item?.texto || '').toUpperCase()
    const arg0 = String(getMenuItemCatalog(item) || '').toUpperCase()
    return (
      item?.id === 14 ||
      item?.id === 15 ||
      key === '2COJINESRESPALDO' ||
      key === '3COJINES60X602COJINES50X50' ||
      key.includes('COJIN') ||
      text.includes('COJIN') ||
      arg0.includes('COJIN')
    )
  }
  const fallbackCushionsItems =
    menuData?.submenus?.filter((item: any) => isSpecialCushionItem(item)) || []
  const armsItems = apiArmsItems.length > 0 ? apiArmsItems : fallbackArmsItems
  const cushionsItems = apiCushionsItems.length > 0 ? apiCushionsItems : fallbackCushionsItems
  const rectangularCushionOption =
    cushionsItems.find(
      (ci) =>
        ci?.id === 14 ||
        String(ci?.claveMenuItem || '').toUpperCase() === '2COJINESRESPALDO' ||
        String(ci?.texto || '')
          .toUpperCase()
          .includes('RECTANG'),
    ) || null
  const multicojinesFallback =
    cushionsItems.find(
      (ci) =>
        ci?.id === 15 ||
        ci?.claveMenuItem === '3COJINES60X602COJINES50X50' ||
        String(ci?.claveMenuItem || '')
          .toUpperCase()
          .includes('COJINES'),
    ) || null

  // Limpiar selección si no hay items seleccionados
  useEffect(() => {
    if (selectedItems.size === 0) {
      setSelectedCushion(null)
    }
  }, [selectedItems.size])

  // Verificar si el item está actualmente seleccionado o existe en el proyecto
  const isItemSelected = (claveMenuItem: string) => {
    if (activeView === 'cushions') {
      return selectedCushion === claveMenuItem
    }

    const relaxParent = resolveParentForNewArm()
    if (relaxParent && isRelaxIntegratedSceneItem(relaxParent, relaxBaseCodes)) {
      const clickedBrNumber = parseRelaxArmButtonCode(claveMenuItem)
      if (clickedBrNumber == null) return false
      const parentCatalog = getRelaxCatalogFromSceneItem(relaxParent)
      const currentVariant = parseRelaxArmVariantCatalog(parentCatalog)
      return currentVariant?.brNumber === clickedBrNumber
    }

    // Para brazos: encontrar el catalogID correspondiente a este claveMenuItem
    const menuItem = armsItems.find((item) => item.claveMenuItem === claveMenuItem)
    const targetCatalogID =
      menuItem && menuItem.args && menuItem.args.length > 0 ? menuItem.args[0] : ''

    if (!targetCatalogID) return false
    const targetArmBaseCode =
      String(targetCatalogID)
        .toUpperCase()
        .match(/^BR\d+/)?.[0] || ''

    // Verificar si existe AL MENOS UN brazo con mismo código base (BRx),
    // incluyendo variantes por fondo (BRx-100 / BRx-105).
    const armExistsInProject = Array.from(items.values()).some((item: any) => {
      if (!item || item.type !== 'linkedItem') return false
      const itemCatalog = String(item.catalogID || '').toUpperCase()
      if (!/^BR\d+(-\d+)?$/i.test(itemCatalog)) return false
      const itemArmBaseCode = itemCatalog.match(/^BR\d+/)?.[0] || ''
      return itemArmBaseCode === targetArmBaseCode
    })

    // Mostrar como seleccionado si existe en el proyecto O si está actualmente seleccionado
    const isCurrentlySelected = Array.from(selectedItems).some((itemId) => {
      const item = items.get(itemId) as any
      if (!item) return false
      const itemCatalog = String(item.catalogID || '').toUpperCase()
      const itemArmBaseCode = itemCatalog.match(/^BR\d+/)?.[0] || ''
      return itemArmBaseCode === targetArmBaseCode
    })

    return armExistsInProject || isCurrentlySelected
  }

  const isCushionCatalog = (catalogID: any) => {
    const c = String(catalogID || '').toUpperCase()
    return c.includes('COJIN')
  }
  const normalizeCatalog = (value: any) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '')
  const normalizeBaseCatalog = (value: any) => String(value || '').trim()
  const resolveArmCatalogByParentDepth = (baseArmCatalog: string, parentItem: any) => {
    const baseMatch = String(baseArmCatalog || '')
      .trim()
      .toUpperCase()
      .match(/^BR\d+/)
    if (!baseMatch) return baseArmCatalog
    const baseCode = baseMatch[0]

    const rawDepthCandidate =
      parentItem?.fondo ??
      parentItem?.depthValue ??
      parentItem?.long ??
      parentItem?.thickness ??
      parentItem?.profundidad ??
      null
    const numericDepth = Number(rawDepthCandidate)
    const catalogText = String(parentItem?.catalogID || '')
    const catalogDepthMatch = catalogText.match(/-(\d{2,3})$/)
    const catalogDepthCm = catalogDepthMatch ? Number(catalogDepthMatch[1]) : 0
    const catalogDepthMm = catalogDepthCm ? catalogDepthCm * 10 : 0

    // Normalizamos a mm:
    // - Si viene en cm (ej: 97, 102, 150) => *10
    // - Si ya viene en mm (ej: 970, 1020, 1500) => usar directo
    const depthMmFromNumeric =
      Number.isFinite(numericDepth) && numericDepth > 0
        ? numericDepth <= 300
          ? numericDepth * 10
          : numericDepth
        : 0
    const depthMm = depthMmFromNumeric > 0 ? depthMmFromNumeric : catalogDepthMm
    if (!Number.isFinite(depthMm) || depthMm <= 0) return baseCode
    // Regla solicitada:
    // - 95 => BRx
    // - 100 (thickness ~970) => BRx-100
    // - 102/150 => BRx-105
    if (depthMm > 1000) return `${baseCode}-105`
    if (catalogDepthCm === 100 || catalogText.includes('-100')) return `${baseCode}-100`
    return baseCode
  }
  const getSeatCatalogByBaseAndStitch = (baseCatalogID: string, stitch: SeatStitchOption) => {
    const base = childBaseCatalogFromUnit(baseCatalogID)
    if (!base) return ''
    if (stitch === 'con') return `asiento ${base}`
    return `asientosin ${base}`
  }

  const isSeatCatalog = (catalogID: any) => {
    const c = String(catalogID || '').toUpperCase()
    return c.includes('ASIENTO')
  }
  const getSeatParentItems = () =>
    Array.from(items.values()).filter(isBaseSeatParentSceneItem) as any[]

  const getArmSideSign = (parentItem: any, armItem: any) =>
    getArmSideSignFromPlacement(parentItem, armItem)

  const getWorldPosFromItem = (sceneItem: any) => {
    if (sceneItem?.matrix) {
      const pos = new Vector3()
      sceneItem.matrix.decompose(pos, new Quaternion(), new Vector3())
      return pos
    }
    return new Vector3(
      Number(sceneItem?.x || 0),
      Number(sceneItem?.y || 0),
      Number(sceneItem?.z || 0),
    )
  }

  const findNearestNonArmLinkedItem = (referencePos: Vector3) => {
    const candidates = Array.from(useAppStore.getState().items.values()).filter((it: any) => {
      if (!it || it.type !== 'linkedItem') return false
      const c = String(it.catalogID || '')
      return !/^BR\d+(-\d+)?$/i.test(c)
    }) as any[]
    let best: any = null
    let bestDist = Number.POSITIVE_INFINITY
    candidates.forEach((candidate) => {
      const p = getWorldPosFromItem(candidate)
      const d = referencePos.distanceTo(p)
      if (d < bestDist) {
        best = candidate
        bestDist = d
      }
    })
    return best
  }

  // Unidad recta (no-brazo y no-RELAX) más cercana a una posición. Los brazos BR
  // solo pueden anclarse a unidades rectas; nunca a una RELAX (su brazo es GLB).
  const findNearestStraightAnchor = (referencePos: Vector3) => {
    const candidates = Array.from(useAppStore.getState().items.values()).filter((it: any) => {
      if (!it || it.type !== 'linkedItem') return false
      if (/^BR\d+(-\d+)?$/i.test(String(it.catalogID || ''))) return false
      return !isRelaxIntegratedSceneItem(it, relaxBaseCodes)
    }) as any[]
    let best: any = null
    let bestDist = Number.POSITIVE_INFINITY
    candidates.forEach((candidate) => {
      const p = getWorldPosFromItem(candidate)
      const d = referencePos.distanceTo(p)
      if (d < bestDist) {
        best = candidate
        bestDist = d
      }
    })
    return best
  }

  // Unidad recta a la que pertenece un brazo BR: por `firstParentID` si lo tiene,
  // o por cercanía (brazo huérfano) a la recta más próxima.
  const resolveStraightOwnerOfArm = (arm: any) => {
    const fp = String(arm?.firstParentID || '')
    if (fp) return useAppStore.getState().items.get(fp as any) as any
    return findNearestStraightAnchor(getWorldPosFromItem(arm))
  }

  const resolveParentForNewArm = () => {
    const liveItems = useAppStore.getState().items
    const currentItem = currentObjectID ? (liveItems.get(currentObjectID as any) as any) : null
    if (!currentItem) {
      const fallback = Array.from(liveItems.values()).find((it: any) => {
        if (!it || it.type !== 'linkedItem') return false
        return !/^BR\d+(-\d+)?$/i.test(String(it.catalogID || ''))
      }) as any
      return fallback || null
    }
    // Asiento/cojín seleccionado → unidad padre (160, 167, …).
    if (currentItem.type === 'linkedSon') {
      const parentId = currentItem.firstParentID as AnyID | undefined
      const parentCandidate = parentId ? (liveItems.get(parentId as any) as any) : null
      if (
        parentCandidate?.type === 'linkedItem' &&
        !/^BR\d+(-\d+)?$/i.test(String(parentCandidate?.catalogID || ''))
      ) {
        return parentCandidate
      }
    }
    if (
      currentItem.type === 'linkedItem' &&
      !/^BR\d+(-\d+)?$/i.test(String(currentItem.catalogID || ''))
    ) {
      return currentItem
    }
    if (
      currentItem.type === 'linkedItem' &&
      /^BR\d+(-\d+)?$/i.test(String(currentItem.catalogID || ''))
    ) {
      const parentId = currentItem.firstParentID as AnyID | undefined
      if (parentId) {
        const parentCandidate = (liveItems.get(parentId as any) as any) || null
        if (
          parentCandidate?.type === 'linkedItem' &&
          !/^BR\d+(-\d+)?$/i.test(String(parentCandidate?.catalogID || ''))
        ) {
          return parentCandidate
        }
      }
      return resolveStraightOwnerOfArm(currentItem)
    }
    const fallback = Array.from(liveItems.values()).find((it: any) => {
      if (!it || it.type !== 'linkedItem') return false
      return !/^BR\d+(-\d+)?$/i.test(String(it.catalogID || ''))
    }) as any
    return fallback || null
  }

  const resolveComfortTargetParent = () => {
    const liveItems = useAppStore.getState().items
    const currentItem = currentObjectID ? (liveItems.get(currentObjectID as any) as any) : null
    if (currentItem?.type === 'linkedItem' && isArmCatalog(currentItem.catalogID)) {
      return null
    }
    return resolveParentForNewArm()
  }

  /**
   * Laterales bloqueados de una unidad RELAX por una unidad colisionada en su fila.
   * Usa el mismo contexto de fila que el resto del sistema de brazos:
   * RowSide +1 = eje +X = lado derecho (posición `D`), -1 = lado izquierdo (`I`).
   */
  const computeRelaxBlockedSides = (relaxUnit: any): RelaxSideBlockState => {
    const liveItems = useAppStore.getState().items
    const rowCtx = createArmRowContext(liveItems as any, relaxUnit, {
      getArmSideSign: getArmSideSignFromPlacement,
    })
    return {
      rightBlocked: rowCtx.isNeighborOnSide(relaxUnit, 1),
      leftBlocked: rowCtx.isNeighborOnSide(relaxUnit, -1),
    }
  }

  /**
   * Devuelve una unidad recta (no RELAX) con labio exterior libre donde anclar un
   * brazo BR, de modo que el brazo nunca se dibuje sobre la unidad RELAX integrada.
   */
  const pickStraightExteriorAnchor = (reference: any): any | null => {
    if (!reference?.id) return null
    const liveItems = useAppStore.getState().items
    const rowCtx = createArmRowContext(liveItems as any, reference, {
      getArmSideSign: getArmSideSignFromPlacement,
    })
    const straightLip = rowCtx
      .listExteriorLips()
      .find((lip) => !isRelaxIntegratedSceneItem(lip.unit, relaxBaseCodes))
    if (straightLip?.unit) return straightLip.unit

    const fallback = rowCtx.row.find(
      (unit: any) => !isRelaxIntegratedSceneItem(unit, relaxBaseCodes),
    )
    return fallback ?? null
  }

  const selectSceneUnit = (unit: any) => {
    if (!unit?.id) return
    useAppStore.setState((state: any) => {
      const selected = state.layers[state.currentLayer].selected as Set<string>
      selected.clear()
      selected.add(String(unit.id))
      const childs = Array.from((unit.childs as Set<string>) || [])
      childs.forEach((childId) => selected.add(String(childId)))
      state.currentObjectID = unit.id
    })
  }

  /** Elimina por completo un brazo BR de la escena (incluida la referencia del padre). */
  const removeArmItemFully = (armId: string) => {
    useAppStore.setState((state: any) => {
      const arm = state.items.get(armId) as any
      const parentId = arm?.firstParentID
      if (parentId) {
        const parent = state.items.get(parentId) as any
        if (parent?.childs) parent.childs.delete(armId)
      }
      state.items.delete(armId)
      state.layers[state.currentLayer].items.delete(armId)
      state.layers[state.currentLayer].selected.delete(armId)
      state.sceneBoundingBoxes.delete(armId)
      state.collisionableItems.delete(armId)
      if (String(state.currentObjectID) === String(armId)) state.currentObjectID = ''
    })
  }

  const comfortTargetParent = resolveComfortTargetParent()
  const canApplyComfort = Boolean(
    comfortTargetParent &&
    isComfortVariantEligible(comfortTargetParent.catalogID, comfortTargetParent.thickness),
  )

  useEffect(() => {
    if (!canApplyComfort) {
      setSelectedComfort(null)
      return
    }
    const nextComfort = getComfortOptionFromCatalog(
      comfortTargetParent?.catalogID,
      comfortTargetParent?.thickness,
    )
    setSelectedComfort(nextComfort)
  }, [
    canApplyComfort,
    comfortTargetParent?.id,
    comfortTargetParent?.catalogID,
    comfortTargetParent?.thickness,
  ])

  const notifyComfortOptionUnavailable = (option: ComfortOption) => {
    const optionMeta = COMFORT_OPTIONS.find((entry) => entry.id === option)
    showArmRestrictionNotice(comfortOptionUnavailableMessage(optionMeta?.label ?? option))
  }

  const handleComfortClick = async (option: ComfortOption) => {
    if (isProcessingComfort.current || !canApplyComfort) return

    const parent = resolveComfortTargetParent()
    if (!parent?.id) return

    const optionMeta = COMFORT_OPTIONS.find((entry) => entry.id === option)
    const nextCatalogID = buildCatalogWithDepthCm(parent.catalogID, optionMeta?.depthCm ?? 95)
    if (!nextCatalogID) {
      notifyComfortOptionUnavailable(option)
      return
    }

    if (nextCatalogID === String(parent.catalogID || '').trim()) {
      setSelectedComfort(option)
      return
    }

    isProcessingComfort.current = true
    const previousComfort = selectedComfort
    const parentIdBeforeReplace = String(parent.id)
    try {
      const exists = await comfortVariantExistsInCatalog(nextCatalogID)
      if (!exists) {
        notifyComfortOptionUnavailable(option)
        setSelectedComfort(previousComfort)
        return
      }

      await withCameraFitBlocked(async () => {
        const parentAtReplace = resolveComfortTargetParent()
        if (!parentAtReplace?.id) return

        let newItemId: AnyID | null = null
        try {
          newItemId = (await replaceItem(
            String(parentAtReplace.id),
            nextCatalogID,
            parentAtReplace.icono || '',
            'linkedItem',
          )) as AnyID | null
        } catch (replaceError) {
          console.warn('[ArmsCushionsPanel] Sustitución confort fallida:', replaceError)
          newItemId = null
        }

        if (!newItemId) {
          notifyComfortOptionUnavailable(option)
          setSelectedComfort(previousComfort)
          const originalStillThere = useAppStore
            .getState()
            .items.get(parentIdBeforeReplace as AnyID)
          if (originalStillThere) {
            selectFullGroups([parentIdBeforeReplace])
            setCurrentObjectID(parentIdBeforeReplace as AnyID)
          }
          return
        }

        setSelectedComfort(option)

        selectFullGroups([String(newItemId)])
        setCurrentObjectID(newItemId as AnyID)
        forceSceneRefresh()
      })
    } catch (error) {
      console.warn('[ArmsCushionsPanel] Error sustituyendo variante de confort:', error)
      notifyComfortOptionUnavailable(option)
      setSelectedComfort(previousComfort)
      const originalStillThere = useAppStore.getState().items.get(parentIdBeforeReplace as AnyID)
      if (originalStillThere) {
        selectFullGroups([parentIdBeforeReplace])
        setCurrentObjectID(parentIdBeforeReplace as AnyID)
      }
    } finally {
      isProcessingComfort.current = false
      forceSceneRefresh()
    }
  }

  const normalizeSelectionToParentForArms = () => {
    const parent = resolveParentForNewArm()
    if (!parent?.id) return null
    useAppStore.setState((state: any) => {
      const selected = state.layers[state.currentLayer].selected as Set<string>
      selected.clear()
      selected.add(String(parent.id))
      const childs = Array.from((parent.childs as Set<string>) || [])
      childs.forEach((childId) => selected.add(String(childId)))
      state.currentObjectID = parent.id
    })
    return parent
  }

  const isArmDrawAbortedError = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error || '')
    return message.includes('drawItem aborted:')
  }

  const getPrimaryMaterialFromArm = (armItem: any) => {
    if (!armItem) return null
    const preferredTexture =
      armItem.textures?.find(
        (t: any) =>
          t?.zoneDisplayName === 'Estructura' ||
          t?.zoneDisplayName === 'Armazon' ||
          t?.zoneDisplayName === 'ArmazonBlanco',
      ) || armItem.textures?.[0]
    if (preferredTexture?.zoneName && armItem?.zones?.[preferredTexture.zoneName]?.material) {
      return { ...(armItem.zones[preferredTexture.zoneName].material as any) }
    }
    const firstZoneKey = Object.keys(armItem?.zones || {})[0]
    if (firstZoneKey && armItem?.zones?.[firstZoneKey]?.material) {
      return { ...(armItem.zones[firstZoneKey].material as any) }
    }
    return null
  }

  const applyMaterialToArmZones = (armId: AnyID, material: any) => {
    if (!material) return
    const liveArm = useAppStore.getState().items.get(armId as any) as any
    if (!liveArm) return

    const zoneNamesFromZones = Object.keys(liveArm?.zones || {})
    const zoneNamesFromTextures = Array.isArray(liveArm?.textures)
      ? liveArm.textures.map((t: any) => t?.zoneName).filter(Boolean)
      : []
    const zoneNames = Array.from(new Set([...zoneNamesFromZones, ...zoneNamesFromTextures]))

    zoneNames.forEach((zoneName) => {
      editZone(armId as any, zoneName as any, { ...material })
    })
  }
  const forceSceneRefresh = () => {
    const previousFrameloop = useAppStore.getState().frameloop
    setFrameloop('always')
    if (frameloopTimeoutRef.current) {
      clearTimeout(frameloopTimeoutRef.current)
      frameloopTimeoutRef.current = undefined
    }
    frameloopTimeoutRef.current = setTimeout(() => {
      setFrameloop(previousFrameloop)
      frameloopTimeoutRef.current = undefined
    }, 900)

    if (typeof window === 'undefined') return
    const r3f = (window as any).r3f
    if (!r3f?.invalidate) return
    r3f.invalidate()
    requestAnimationFrame(() => r3f.invalidate?.())
    setTimeout(() => r3f.invalidate?.(), 40)
    setTimeout(() => r3f.invalidate?.(), 120)
  }

  const selectFullGroups = (baseIds: string[]) => {
    const uniqueBaseIds = Array.from(new Set(baseIds.filter(Boolean)))
    if (uniqueBaseIds.length === 0) return
    useAppStore.setState((state: any) => {
      const selected = state.layers[state.currentLayer].selected as Set<string>
      selected.clear()
      uniqueBaseIds.forEach((baseId) => {
        selected.add(baseId)
        const baseItem = state.items.get(baseId) as any
        const childs = Array.from((baseItem?.childs as Set<string>) || [])
        childs.forEach((childId) => selected.add(childId))
      })
    })
    setCurrentObjectID(uniqueBaseIds[0] as any)
  }

  // Inicializar cojín activo desde localStorage o desde la escena actual.
  useEffect(() => {
    if (!cushionsItems.length) return
    const sceneCushions = Array.from(items.values()).filter(
      (it: any) => it?.type === 'linkedSon' && isCushionCatalog((it as any).catalogID),
    ) as any[]
    const currentItem = currentObjectID ? (items.get(currentObjectID as any) as any) : null
    const currentParentId =
      currentItem?.type === 'linkedItem'
        ? String(currentItem.id)
        : currentItem?.type === 'linkedSon'
          ? String(currentItem.firstParentID || '')
          : ''
    const currentParentCushion = currentParentId
      ? sceneCushions.find((it: any) => String(it?.firstParentID || '') === currentParentId)
      : null

    const hasRectangularSceneCushion = sceneCushions.some((it: any) =>
      normalizeCatalog(it?.catalogID || it?.referencia).includes('cojinesr'),
    )
    const hasStandardSceneCushion = sceneCushions.some((it: any) => {
      const normalized = normalizeCatalog(it?.catalogID || it?.referencia)
      return normalized.includes('cojin') && !normalized.includes('cojinesr')
    })

    const fallbackFromScene =
      currentParentCushion ||
      (hasRectangularSceneCushion && !hasStandardSceneCushion
        ? sceneCushions.find((it: any) =>
            normalizeCatalog(it?.catalogID || it?.referencia).includes('cojinesr'),
          )
        : sceneCushions[0]) ||
      null
    const activeCatalog = String(
      fallbackFromScene?.catalogID || fallbackFromScene?.referencia || '',
    ).trim()
    if (!activeCatalog) {
      setSelectedCushion(null)
      return
    }
    const normalizedActiveCatalog = normalizeCatalog(activeCatalog)
    const isRectangularSceneCushion = normalizedActiveCatalog.includes('cojinesr')
    const isAnySceneCushion = normalizedActiveCatalog.includes('cojin')
    const match = cushionsItems.find((ci) => {
      const byArgs = normalizeCatalog(getMenuItemCatalog(ci))
      const byKey = normalizeCatalog(ci?.claveMenuItem)
      const byText = normalizeCatalog(ci?.texto)
      return (
        byArgs === normalizedActiveCatalog ||
        byKey === normalizedActiveCatalog ||
        byText === normalizedActiveCatalog
      )
    })
    if (match) {
      setSelectedCushion(match.claveMenuItem)
    } else {
      // Si el cojín real existe en escena pero no mapea exacto al menú, usar fallback visual.
      if (isRectangularSceneCushion && rectangularCushionOption?.claveMenuItem) {
        setSelectedCushion(rectangularCushionOption.claveMenuItem)
      } else if (isAnySceneCushion && multicojinesFallback?.claveMenuItem) {
        setSelectedCushion(multicojinesFallback.claveMenuItem)
      } else {
        setSelectedCushion(null)
      }
    }
  }, [cushionsItems, items, currentObjectID, multicojinesFallback, rectangularCushionOption])

  useEffect(() => {
    const parents = getSeatParentItems()
    if (parents.length === 0) {
      setSelectedSeatHeightCm('42')
      setSelectedSeatStitch(null)
      return
    }
    const resolvedHeight = resolveSceneSeatHeightCmForDisplay(items)
    const nextHeight: SeatHeightOption = resolvedHeight === 47 ? '47' : '42'
    const referenceParent =
      parents.find((parent: any) => String(parent?.seatStitchType || '').trim()) || parents[0]
    const rawStitch = String((referenceParent as any)?.seatStitchType || '')
      .trim()
      .toLowerCase()
    const nextStitch: SeatStitchOption = rawStitch === 'sin' ? 'sin' : 'con'
    setSelectedSeatHeightCm(nextHeight)
    setSelectedSeatStitch(nextStitch)
  }, [items.size])

  const applySeatHeightSelection = async (nextHeight: SeatHeightOption) => {
    if (isProcessingSeatHeight.current) return
    const targetHeightCm = Number(nextHeight)
    const parents = getSeatParentItems()
    if (parents.length === 0) return

    const alreadyApplied = parents.every((parent: any) => {
      const currentCatalog = String(parent?.catalogID || '').trim()
      const expectedCatalog = buildCatalogWithSeatHeightCm(currentCatalog, targetHeightCm)
      return (
        expectedCatalog === currentCatalog &&
        Number(parent?.seatHeightCm || 0) === targetHeightCm
      )
    })
    if (alreadyApplied && selectedSeatHeightCm === nextHeight) return

    isProcessingSeatHeight.current = true
    const previousHeight = selectedSeatHeightCm
    const affectedBaseIds: string[] = []
    const skippedUnits: string[] = []

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(GLOBAL_SEAT_HEIGHT_KEY, nextHeight)
      }

      await runWithHistoryBatchAsync(async () => {
        await withCameraFitBlocked(async () => {
          for (const parent of parents) {
            const parentId = String(parent.id)
            const currentCatalog = String(parent?.catalogID || '').trim()
            if (!currentCatalog) continue

            const nextCatalogID = buildCatalogWithSeatHeightCm(currentCatalog, targetHeightCm)

            if (nextCatalogID === currentCatalog) {
              editItem(parent.id as any, { seatHeightCm: targetHeightCm } as any)
              affectedBaseIds.push(parentId)
              continue
            }

            const exists = await seatHeightVariantExistsInCatalog(nextCatalogID)
            if (!exists) {
              skippedUnits.push(currentCatalog)
              continue
            }

            let newItemId: AnyID | null = null
            try {
              newItemId = (await replaceItem(
                parentId,
                nextCatalogID,
                parent.icono || '',
                'linkedItem',
                undefined,
                { preserveSeatCushionChildren: true, targetSeatHeightCm: targetHeightCm },
              )) as AnyID | null
            } catch (replaceError) {
              console.warn('[ArmsCushionsPanel] Sustitución altura asiento fallida:', replaceError)
              newItemId = null
            }

            if (!newItemId) {
              skippedUnits.push(currentCatalog)
              continue
            }

            editItem(newItemId as any, { seatHeightCm: targetHeightCm } as any)
            affectedBaseIds.push(String(newItemId))
          }

          if (affectedBaseIds.length === 0) {
            setSelectedSeatHeightCm(previousHeight)
            if (skippedUnits.length > 0) {
              showArmRestrictionNotice(seatHeightOptionUnavailableMessage(targetHeightCm))
            }
            return
          }

          setSelectedSeatHeightCm(nextHeight)
          selectFullGroups(affectedBaseIds)
          forceSceneRefresh()

          if (skippedUnits.length > 0) {
            showArmRestrictionNotice(
              `${seatHeightOptionUnavailableMessage(targetHeightCm)} ${ui('unitsSkipped')} ${skippedUnits.join(', ')}`,
            )
          }
        })
      })
    } catch (error) {
      console.warn('[ArmsCushionsPanel] Error sustituyendo altura de asiento:', error)
      setSelectedSeatHeightCm(previousHeight)
      showArmRestrictionNotice(seatHeightOptionUnavailableMessage(targetHeightCm))
    } finally {
      isProcessingSeatHeight.current = false
      forceSceneRefresh()
    }
  }

  const applySeatStitchSelection = async (nextStitch: SeatStitchOption) => {
    setSelectedSeatStitch(nextStitch)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(GLOBAL_SEAT_STITCH_KEY, nextStitch)
    }
    const parents = getSeatParentItems()
    parents.forEach((parent: any) => {
      editItem(parent.id as any, { seatStitchType: nextStitch } as any)
    })

    // Reemplazo global de hijo asiento en todos los padres reales.
    // No depender de packGroupId porque hay unidades (ej: 1-95) sin pack.
    const groups = getSeatParentItems()
      .map((parent: any) => [String(parent.id), parent] as [string, any])
      .filter(([, parent]) => Number((parent?.childs as Set<string> | undefined)?.size || 0) > 0)
    const affectedBaseIds: string[] = []

    for (const [baseId, baseItem] of groups) {
      const baseCatalogID = String(baseItem?.catalogID || '')
      const targetSeatCatalogID = getSeatCatalogByBaseAndStitch(baseCatalogID, nextStitch)
      if (!targetSeatCatalogID) continue
      const childIds = Array.from((baseItem.childs as Set<string>) || [])
      const oldSeatId = childIds.find((childId) => {
        const child: any = items.get(childId as any)
        return child?.type === 'linkedSon' && isSeatCatalog(child.catalogID)
      })
      if (!oldSeatId) continue
      const oldSeatItem: any = items.get(oldSeatId as any)
      if (!oldSeatItem?.matrix) continue

      let newSeatId: string | null = null
      try {
        newSeatId = (await drawItem(
          getId('linkedSon'),
          targetSeatCatalogID,
          oldSeatItem?.icono || '',
          true,
          new Matrix4(),
          'linkedSon',
        )) as string
      } catch (error) {
        console.error('[SeatStitch] drawItem error', error)
      }
      if (!newSeatId) continue

      editItem(
        newSeatId as any,
        {
          firstParentID: baseId,
          depth: 1,
          x: 0,
          y: 0,
          z: 0,
          matrix: new Matrix4(),
          isBlocked: true,
        } as any,
      )
      setCollision(false, newSeatId as any)

      const nextChilds = new Set(childIds.filter((id) => id !== oldSeatId))
      nextChilds.add(newSeatId as string)
      editItem(baseId as any, { childs: nextChilds } as any)

      useAppStore.setState((state: any) => {
        state.items.delete(oldSeatId)
        state.layers[state.currentLayer].items.delete(oldSeatId)
        state.layers[state.currentLayer].selected.delete(oldSeatId)
        state.sceneBoundingBoxes.delete(oldSeatId)
        state.collisionableItems.delete(oldSeatId)
      })
      affectedBaseIds.push(baseId as string)
      forceSceneRefresh()
    }
    selectFullGroups(affectedBaseIds)
    forceSceneRefresh()
  }

  // Reemplaza todos los brazos existentes en el proyecto por el catálogo indicado.
  // Usada tanto por el botón "Reemplazar brazos por este" como por el fallback
  // automático cuando ya no se puede dibujar un brazo nuevo (ver handleArmClick).
  const replaceArmsInProjectWithCatalog = async (
    baseCatalogID: string,
    realCatalogID: string | undefined,
    icono: string,
  ) => {
    const baseUnitsInScene = Array.from(items.values()).filter((sceneItem: any) => {
      if (!sceneItem || sceneItem.type !== 'linkedItem') return false
      return !/^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))
    }) as any[]
    console.log('[ArmDepthDebug][replace:start]', {
      clickedBaseCatalogID: baseCatalogID,
      currentObjectID,
      baseUnits: baseUnitsInScene.map((u) => ({
        id: u.id,
        catalogID: u.catalogID,
        thickness: u.thickness,
        firstParentID: (u as any).firstParentID || null,
      })),
    })

    // Para brazos: reemplazar todos los brazos existentes en el proyecto
    const existingArms = Array.from(items.values()).filter((sceneItem: any) => {
      if (!sceneItem || sceneItem.type !== 'linkedItem') return false
      return /^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))
    })

    for (const arm of existingArms) {
      // IMPORTANTE: preservar acabado/textura actual del brazo reemplazado.
      const previousArmMaterial = getPrimaryMaterialFromArm(arm)
      const oldPos = getWorldPosFromItem(arm)
      const parentId = (arm as any)?.firstParentID as AnyID | undefined
      const parentBefore = parentId ? (items.get(parentId as any) as any) : null
      const resolvedCatalogID = resolveArmCatalogByParentDepth(baseCatalogID, parentBefore)
      console.log('[ArmDepthDebug][replace:per-arm]', {
        armId: arm.id,
        armCurrentCatalogID: arm.catalogID,
        parentId,
        parentCatalogID: parentBefore?.catalogID,
        parentThickness: parentBefore?.thickness,
        targetParentId: parentBefore?.id,
        targetParentCatalogID: parentBefore?.catalogID,
        targetParentThickness: parentBefore?.thickness,
        resolvedCatalogID,
      })
      const sideSign = parentBefore ? getArmSideSign(parentBefore, arm) : 1
      const newArmId = await replaceItem(
        arm.id as any,
        resolvedCatalogID,
        icono || '',
        'items',
        realCatalogID,
      )
      if (newArmId) {
        const liveStore = useAppStore.getState()
        const parentNow = parentId ? (liveStore.items.get(parentId as any) as any) : parentBefore
        let hasAppliedApproach = false
        if (parentNow && parentId && collideItems) {
          collideItems(newArmId as AnyID, parentId as AnyID)
          hasAppliedApproach = true
        }
        // Si no hay padre, mantener SIEMPRE posición del brazo anterior.
        if (!hasAppliedApproach) {
          const liveArm = liveStore.items.get(newArmId as any) as any
          if (liveArm?.matrix) {
            const q = new Quaternion()
            liveArm.matrix.decompose(new Vector3(), q, new Vector3())
            const m = new Matrix4()
              .makeRotationFromQuaternion(q)
              .setPosition(oldPos.x, oldPos.y, oldPos.z)
            editItem(
              newArmId as any,
              { matrix: m, x: oldPos.x, y: oldPos.y, z: oldPos.z, depth: 1 } as any,
            )
          } else {
            editItem(newArmId as any, { x: oldPos.x, y: oldPos.y, z: oldPos.z, depth: 1 } as any)
          }
          const anchor = findNearestNonArmLinkedItem(oldPos)
          if (anchor?.id && collideItems) {
            collideItems(newArmId as AnyID, anchor.id as AnyID)
          }
        }
      }
      if (newArmId && previousArmMaterial) {
        setTimeout(() => {
          applyMaterialToArmZones(newArmId as any, previousArmMaterial)
          forceSceneRefresh()
        }, 0)
      }
    }
    // Un único tipo de brazo en escena: unificar también las RELAX integradas.
    const brMatch = String(baseCatalogID).toUpperCase().match(/^BR(\d+)/)
    if (brMatch) await retypeRelaxVariantsToBr(Number(brMatch[1]))
    forceSceneRefresh()
  }

  // Manejar el reemplazo de item
  const handleReplaceItem = async (e: React.MouseEvent, item: Submenu) => {
    e.stopPropagation()

    const baseCatalogID = item.args && item.args.length > 0 ? item.args[0] : ''
    const realCatalogID = item.args && item.args.length === 2 ? item.args[0] : undefined

    if (!baseCatalogID) return

    if (activeView === 'arms') {
      await replaceArmsInProjectWithCatalog(baseCatalogID, realCatalogID, item.icono || '')
    } else {
      // Para cojines: lógica existente (reemplazar solo el seleccionado)
      if (!currentObjectID) return
      const usingItem = items.get(currentObjectID)
      if (!usingItem) return

      await withCameraFitBlocked(async () => {
        await replaceItem(usingItem.id, baseCatalogID, item.icono || '', 'items', realCatalogID)
      })
    }
  }

  /**
   * Aplica el cambio de brazo (por cambio de GLB) a una unidad RELAX y devuelve el
   * número de brazo (BRx) que quedó montado, o `null` si la unidad quedó sin brazo
   * o si la acción no produjo cambios (noop/bloqueada). El valor de retorno permite
   * al llamante unificar el resto de brazos sin depender de releer `currentObjectID`.
   */
  const handleRelaxIntegratedArmClick = async (
    claveMenuItem: string,
    explicitParent?: any,
    options?: { manageSelection?: boolean },
  ): Promise<number | null> => {
    const parent = explicitParent ?? resolveParentForNewArm()
    if (!parent?.id) return null

    const clickedBrNumber = parseRelaxArmButtonCode(claveMenuItem)
    if (clickedBrNumber == null) return null

    // Restringir el cambio de GLB según los laterales colisionados de la unidad RELAX:
    // ambos lados ocupados → bloqueado; un lado ocupado → solo el lado libre o sin brazos.
    const blockedSides = computeRelaxBlockedSides(parent)

    const resolution = resolveConstrainedRelaxArmVariant(
      getRelaxCatalogFromSceneItem(parent),
      clickedBrNumber,
      blockedSides,
    )

    if (resolution.kind === 'noop') return null
    if (resolution.kind === 'blocked') {
      showArmRestrictionNotice(RELAX_ARM_NOTICE[resolution.reason])
      return null
    }

    const nextCatalogID = resolution.nextCatalogID
    const appliedBrNumber = parseRelaxArmVariantCatalog(nextCatalogID)?.brNumber ?? null

    const variantMenuItem = findRelaxArmVariantInMenu(relaxArmVariants, nextCatalogID)
    const baseMenuItem = findRelaxBaseUnitInMenu(relaxMenuData?.submenus ?? [], nextCatalogID)
    const catalogFromMenu = variantMenuItem
      ? String(
          variantMenuItem.codigoUnidad || getMenuItemCatalog(variantMenuItem) || nextCatalogID,
        ).trim()
      : baseMenuItem
        ? String(
            baseMenuItem.codigoUnidad || getMenuItemCatalog(baseMenuItem) || nextCatalogID,
          ).trim()
        : nextCatalogID

    const currentCatalog = getRelaxCatalogFromSceneItem(parent)
    if (currentCatalog.toUpperCase() === catalogFromMenu.toUpperCase()) return appliedBrNumber

    await withCameraFitBlocked(async () => {
      const newItemId = await replaceItem(
        String(parent.id),
        catalogFromMenu,
        variantMenuItem?.icono || baseMenuItem?.icono || parent.icono || '',
        'linkedItem',
      )
      if (!newItemId) return

      if (options?.manageSelection !== false) {
        selectFullGroups([String(newItemId)])
        setCurrentObjectID(newItemId as AnyID)
      }
      forceSceneRefresh()
    })

    return appliedBrNumber
  }

  // Unifica el tipo de TODOS los brazos BR existentes al tipo indicado (regla por
  // fondo incluida), conservando su lado y material actual.
  const retypeExistingArmsTo = async (
    baseCatalogID: string,
    realCatalogID: string | undefined,
    icono: string,
  ) => {
    const liveStoreAtStart = useAppStore.getState()
    const existingArms = Array.from(liveStoreAtStart.items.values()).filter((sceneItem: any) => {
      if (!sceneItem || sceneItem.type !== 'linkedItem') return false
      return /^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))
    }) as any[]

    for (const existingArm of existingArms) {
      const currentCatalogID = String(existingArm.catalogID || '')
      const parentId = (existingArm as any)?.firstParentID as AnyID | undefined
      const parentBefore = parentId ? (liveStoreAtStart.items.get(parentId as any) as any) : null

      // Con padre → resolvemos el fondo por el padre; sin padre (brazo huérfano)
      // conservamos el sufijo de fondo del propio brazo para no perder la variante.
      let targetCatalogID: string
      if (parentBefore) {
        targetCatalogID = resolveArmCatalogByParentDepth(baseCatalogID, parentBefore)
      } else {
        const newBaseCode = String(baseCatalogID).match(/^BR\d+/i)?.[0] ?? baseCatalogID
        const depthSuffix = currentCatalogID.match(/-\d{2,3}$/)?.[0] ?? ''
        targetCatalogID = `${newBaseCode}${depthSuffix}`
      }
      if (targetCatalogID.toUpperCase() === currentCatalogID.toUpperCase()) continue

      const previousArmMaterial = getPrimaryMaterialFromArm(existingArm)
      const replacedArmId = await replaceItem(
        existingArm.id as any,
        targetCatalogID,
        icono,
        'items',
        realCatalogID,
      )
      if (!replacedArmId) continue

      // Padre para colisionar: el `firstParentID` si sigue vivo; si no (brazo
      // huérfano), la recta más cercana a la posición actual del brazo.
      const liveArmForAnchor = useAppStore.getState().items.get(replacedArmId as any) as any
      let liveParent = parentId
        ? (useAppStore.getState().items.get(parentId as any) as any)
        : null
      if (!liveParent && liveArmForAnchor) {
        liveParent = findNearestStraightAnchor(getWorldPosFromItem(liveArmForAnchor))
      }
      if (liveParent && collideItems) {
        // Restaurar el vínculo padre→brazo si estaba huérfano, para que futuras
        // detecciones y colisiones funcionen por la vía rápida.
        if (!parentId) {
          editItem(replacedArmId as any, { firstParentID: liveParent.id } as any)
        }
        collideItems(replacedArmId as AnyID, liveParent.id as AnyID)
      }
      if (previousArmMaterial) {
        setTimeout(() => {
          applyMaterialToArmZones(replacedArmId as any, previousArmMaterial)
          forceSceneRefresh()
        }, 0)
      }
    }
  }

  // Unifica el número de brazo (BRx) de TODAS las unidades RELAX con variante de
  // brazo en escena, conservando su lado/posición (D/A/I). Así nunca conviven
  // dos tipos de brazo distintos entre brazos BR y brazos integrados por GLB.
  const retypeRelaxVariantsToBr = async (brNumber: number) => {
    const relaxVariantUnits = Array.from(useAppStore.getState().items.values()).filter(
      (sceneItem: any) => {
        if (!sceneItem || sceneItem.type !== 'linkedItem') return false
        const parts = parseRelaxArmVariantCatalog(getRelaxCatalogFromSceneItem(sceneItem))
        return parts != null && parts.brNumber !== brNumber
      },
    ) as any[]

    for (const unit of relaxVariantUnits) {
      const liveUnit = useAppStore.getState().items.get(unit.id as any) as any
      const parts = parseRelaxArmVariantCatalog(getRelaxCatalogFromSceneItem(liveUnit))
      if (!parts) continue
      const targetCatalog = buildRelaxArmVariantCatalog(parts.baseCode, brNumber, parts.position)
      const variantMenuItem = findRelaxArmVariantInMenu(relaxArmVariants, targetCatalog)
      const catalogFromMenu = variantMenuItem
        ? String(
            variantMenuItem.codigoUnidad || getMenuItemCatalog(variantMenuItem) || targetCatalog,
          ).trim()
        : targetCatalog
      if (getRelaxCatalogFromSceneItem(liveUnit).toUpperCase() === catalogFromMenu.toUpperCase()) {
        continue
      }
      await withCameraFitBlocked(async () => {
        await replaceItem(
          String(liveUnit.id),
          catalogFromMenu,
          variantMenuItem?.icono || liveUnit.icono || '',
          'linkedItem',
        )
      })
    }
  }

  // Regla global: en escena solo puede existir un tipo de brazo (BRx). Unifica a
  // `brNumber` TODOS los brazos: los BR sueltos de unidades rectas y los brazos
  // integrados por cambio de GLB de las unidades RELAX, conservando lado y fondo.
  const unifyAllArmsToBrNumber = async (brNumber: number, icono: string) => {
    await retypeExistingArmsTo(`BR${brNumber}`, undefined, icono)
    await retypeRelaxVariantsToBr(brNumber)
  }

  // Dibuja un nuevo brazo BR anclado al padre indicado y hereda el acabado del padre.
  const drawAndInheritArm = async (
    parentForNewArm: any,
    item: Submenu,
    baseCatalogID: string,
    realCatalogID: string | undefined,
  ) => {
    if (!parentForNewArm) return

    const resolvedCatalogID = resolveArmCatalogByParentDepth(baseCatalogID, parentForNewArm)
    try {
      await runWithHistoryBatchAsync(async () => {
        let newArmId: AnyID | null = null
        try {
          newArmId = (await drawItem(
            getId('linkedItem'),
            resolvedCatalogID,
            item.icono || '',
            true,
            null,
            undefined,
            item.texto,
            false,
            { preDrawSelectedAnchorId: parentForNewArm.id as AnyID },
          )) as AnyID | null
        } catch (error) {
          if (isArmDrawAbortedError(error)) throw error
          console.warn('⚠️ [ArmsCushionsPanel] Error al dibujar brazo:', error)
          return
        }

        // IMPORTANTE: leer del store vivo, no del snapshot `items` de render (que aún
        // no contiene el brazo recién dibujado en este handler asíncrono). Si se lee del
        // snapshot, `createdArm` es null y nunca se fija `firstParentID` (el brazo queda
        // huérfano → no se detecta «ya armada» y se duplican brazos en cada clic).
        const liveItems = useAppStore.getState().items
        const selectedParent =
          parentForNewArm || (currentObjectID ? (liveItems.get(currentObjectID) as any) : null)
        const createdArm = newArmId ? (liveItems.get(newArmId as any) as any) : null
        if (!selectedParent || !createdArm || selectedParent.type !== 'linkedItem') return

        // Garantizar el vínculo padre→brazo: si el snap no fijó `firstParentID`, lo
        // asignamos al padre indicado para que el brazo no quede huérfano (lo que
        // rompería la detección de «la recta ya tiene brazo» y su recolocación).
        if (!String(createdArm.firstParentID || '') && parentForNewArm?.id) {
          editItem(createdArm.id as any, { firstParentID: parentForNewArm.id } as any)
        }

        const hasSelectedTexture = () => {
          if (!selectedParent || !selectedParent.textures) return false
          return selectedParent.textures.some(
            (texture: any) => texture.searchID !== undefined && texture.searchID !== null,
          )
        }

        if (hasSelectedTexture()) {
          const parentTextures = selectedParent.textures as any[]
          const parentTexture =
            parentTextures?.find(
              (t: any) =>
                t?.zoneDisplayName === 'Estructura' ||
                t?.zoneDisplayName === 'Armazon' ||
                t?.zoneDisplayName === 'ArmazonBlanco',
            ) || parentTextures?.[0]
          const armTextures = createdArm.textures as any[]
          const armTexture =
            armTextures?.find(
              (t: any) =>
                t?.zoneDisplayName === 'Estructura' ||
                t?.zoneDisplayName === 'Armazon' ||
                t?.zoneDisplayName === 'ArmazonBlanco',
            ) || armTextures?.[0]

          if (parentTexture?.zoneName && armTexture?.zoneName) {
            const parentMaterial = selectedParent.zones?.[parentTexture.zoneName]?.material
            if (parentMaterial) {
              // Herencia de acabado: parte del mismo gesto que dibujar el brazo (un solo undo).
              editZone(createdArm.id as any, armTexture.zoneName, { ...parentMaterial }, true)
              forceSceneRefresh()
            }
          }
        }
      })
    } catch (error) {
      if (!isArmDrawAbortedError(error)) return
      // Ya no se puede dibujar un brazo nuevo (p. ej. solo se permite un lado):
      // en vez de solo avisar, reemplazamos automáticamente los brazos existentes
      // por el tipo elegido, como si se hubiera pulsado "Reemplazar brazos por este".
      try {
        await replaceArmsInProjectWithCatalog(baseCatalogID, realCatalogID, item.icono || '')
      } catch (replaceError) {
        console.warn(
          '⚠️ [ArmsCushionsPanel] Error al reemplazar brazos tras restricción:',
          replaceError,
        )
        showArmRestrictionNotice(ARM_NOTICE_ONLY_ONE_SIDE())
      }
    }
  }

  /**
   * Grupo (fila/L) con al menos una unidad RELAX. El clic actúa SOBRE LA UNIDAD
   * SELECCIONADA:
   * - RELAX → ciclo de su GLB; si queda armada, unifica tipo BR en rectas del grupo.
   * - Recta (160, …) → coloca/quita/cambia su brazo BR; al poner o cambiar tipo,
   *   sincroniza también las RELAX del grupo al mismo BRx.
   * El BR jamás se ancla sobre la RELAX (su brazo es por cambio de GLB).
   */
  const handleRelaxGroupArmClick = async (
    item: Submenu,
    armReference: any,
    baseCatalogID: string,
    realCatalogID: string | undefined,
  ) => {
    const clickedBrNumber = parseRelaxArmButtonCode(item.claveMenuItem)
    const liveItems = useAppStore.getState().items

    // Preferir la unidad explícitamente seleccionada (no el rinconero ni un hijo).
    let targetUnit = armReference
    if (targetUnit?.type === 'linkedSon' && targetUnit.firstParentID) {
      targetUnit = liveItems.get(targetUnit.firstParentID as any) || targetUnit
    }
    if (isRinconeroCatalog(targetUnit?.catalogID)) {
      const preferredStraight = Array.from(selectedItems)
        .map((id) => liveItems.get(id as any) as any)
        .find(
          (it) =>
            it?.type === 'linkedItem' &&
            !isRinconeroCatalog(it.catalogID) &&
            !/^BR\d+(-\d+)?$/i.test(String(it.catalogID || '')) &&
            !isRelaxIntegratedSceneItem(it, relaxBaseCodes),
        )
      targetUnit =
        preferredStraight || pickStraightExteriorAnchor(targetUnit) || targetUnit
    }

    // Unidad RELAX seleccionada: gestionar su propio brazo por cambio de GLB.
    if (isRelaxIntegratedSceneItem(targetUnit, relaxBaseCodes)) {
      const appliedBrNumber = await handleRelaxIntegratedArmClick(item.claveMenuItem, targetUnit, {
        manageSelection: true,
      })
      // Si la RELAX quedó armada, unificar BR sueltos y otras RELAX al mismo tipo.
      if (appliedBrNumber != null) {
        await unifyAllArmsToBrNumber(appliedBrNumber, item.icono || '')
      }
      return
    }

    // Rinconero u otra unidad no apta: no hay labio BR válido.
    if (
      !targetUnit?.id ||
      isRinconeroCatalog(targetUnit.catalogID) ||
      /^BR\d+(-\d+)?$/i.test(String(targetUnit.catalogID || ''))
    ) {
      showArmRestrictionNotice(
        ui('selectFreeEndUnit'),
      )
      return
    }

    // Unidad recta seleccionada (160, etc.): brazo BR en su labio exterior.
    const ownArms = Array.from(liveItems.values()).filter((sceneItem: any) => {
      if (!sceneItem || sceneItem.type !== 'linkedItem') return false
      if (!/^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))) return false
      const owner = resolveStraightOwnerOfArm(sceneItem)
      return owner?.id != null && String(owner.id) === String(targetUnit.id)
    }) as any[]

    if (ownArms.length > 0) {
      const currentMatch = String(ownArms[0].catalogID || '')
        .toUpperCase()
        .match(/^BR(\d+)/)
      const currentBrNumber = currentMatch ? Number(currentMatch[1]) : null

      // Cambio de tipo de BR: reemplazar y unificar RELAX al mismo BRx.
      if (
        clickedBrNumber != null &&
        currentBrNumber != null &&
        clickedBrNumber !== currentBrNumber
      ) {
        await unifyAllArmsToBrNumber(clickedBrNumber, item.icono || '')
        return
      }

      // Mismo BR: alternar a «sin brazo» en esta recta (no toca la RELAX).
      ownArms.forEach((arm) => removeArmItemFully(String(arm.id)))
      return
    }

    // Recta sin brazo: comprobar que tiene labio exterior libre.
    const rowCtx = createArmRowContext(liveItems as any, targetUnit, {
      getArmSideSign: getArmSideSignFromPlacement,
    })
    const freeLipsOnTarget = rowCtx
      .listExteriorLips()
      .filter((lip) => String(lip.unit.id) === String(targetUnit.id))
    if (freeLipsOnTarget.length === 0) {
      showArmRestrictionNotice(
        ui('noFreeEndForArm'),
      )
      return
    }

    selectSceneUnit(targetUnit)
    // Limpiar snappedSides fantasma: drawItem confía en ellos y puede abortar
    // «sin lado libre» aunque la geometría sí tenga extremo exterior (p. ej. L con RELAX).
    {
      const liveTarget = useAppStore.getState().items.get(targetUnit.id as any) as any
      const ctx = createArmRowContext(useAppStore.getState().items as any, liveTarget || targetUnit, {
        getArmSideSign: getArmSideSignFromPlacement,
      })
      const nextSnapped: Partial<Record<'left' | 'right', string>> = {
        ...(((liveTarget || targetUnit).snappedSides as any) || {}),
      }
      if (!ctx.isNeighborOnSide(liveTarget || targetUnit, -1)) delete nextSnapped.left
      if (!ctx.isNeighborOnSide(liveTarget || targetUnit, 1)) delete nextSnapped.right
      editItem(targetUnit.id as any, { snappedSides: nextSnapped } as any)
    }
    await retypeExistingArmsTo(baseCatalogID, realCatalogID, item.icono || '')
    await drawAndInheritArm(targetUnit, item, baseCatalogID, realCatalogID)

    // Sincronizar RELAX del grupo (y resto de BR) al tipo elegido.
    if (clickedBrNumber != null) {
      await unifyAllArmsToBrNumber(clickedBrNumber, item.icono || '')
    }
  }

  // Manejar click en brazo
  const handleArmClick = async (item: Submenu) => {
    // Protección contra doble ejecución
    if (isProcessingArm.current) {
      console.log(
        '⚠️ [ArmsCushionsPanel] Ya se está procesando un brazo, ignorando click duplicado',
      )
      return
    }

    isProcessingArm.current = true
    const normalizedParent = normalizeSelectionToParentForArms()

    if (!item.args || item.args.length === 0) {
      isProcessingArm.current = false
      return
    }

    // Obtener el catalogID del nuevo tipo de brazo
    const baseCatalogID = item.args[0]
    const realCatalogID = item.args.length === 2 ? item.args[0] : undefined

    // Grupo (fila) de la unidad de referencia: una fila puede mezclar unidades
    // rectas con una unidad especial RELAX. Cada tipo gestiona su brazo distinto:
    // - RELAX → cambio de GLB en su lado libre (colisión-consciente), nunca BR.
    // - Rectas → brazo BR en su labio exterior.
    const armReference = normalizedParent || resolveParentForNewArm()
    const liveItemsForRow = useAppStore.getState().items
    const rowUnits = armReference
      ? getLinkedModulesInRow(liveItemsForRow as any, armReference)
      : []
    const relaxUnitsInRow = rowUnits.filter((unit: any) =>
      isRelaxIntegratedSceneItem(unit, relaxBaseCodes),
    )

    // Fila con RELAX: el clic actúa sobre la unidad seleccionada.
    if (relaxUnitsInRow.length > 0) {
      try {
        await handleRelaxGroupArmClick(item, armReference, baseCatalogID, realCatalogID)
      } catch (error) {
        console.warn('⚠️ [ArmsCushionsPanel] Error en ciclo de brazos del grupo RELAX:', error)
      } finally {
        forceSceneRefresh()
        isProcessingArm.current = false
      }
      return
    }

    // Fila sin RELAX: flujo estándar de brazos BR.
    const straightAnchor = pickStraightExteriorAnchor(armReference)
    const clickedBrNumber = parseRelaxArmButtonCode(item.claveMenuItem)
    const existingBrArms = Array.from(useAppStore.getState().items.values()).filter(
      (sceneItem: any) => {
        if (!sceneItem || sceneItem.type !== 'linkedItem') return false
        return /^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))
      },
    )
    const isBrTypeOnlyChange =
      clickedBrNumber != null &&
      existingBrArms.length > 0 &&
      existingBrArms.some((arm) => {
        const match = String(arm.catalogID || '')
          .toUpperCase()
          .match(/^BR(\d+)/)
        return match && Number(match[1]) !== clickedBrNumber
      })

    // Unidad seleccionada «con mano» (D/I, incluidas orejas): el brazo actúa sobre
    // ESA unidad (un solo brazo, en su lado de mano), no sobre una recta del grupo.
    const selectedIsHanded =
      armReference != null && unitArmHandednessLetter(armReference.catalogID) != null
    try {
      await retypeExistingArmsTo(baseCatalogID, realCatalogID, item.icono || '')

      // Cambio de tipo BR (p. ej. BR1 → BR3) con brazos ya en escena: solo retypar,
      // sin intentar dibujar otro brazo (evita abort + aviso falso).
      if (isBrTypeOnlyChange) {
        if (clickedBrNumber != null) await retypeRelaxVariantsToBr(clickedBrNumber)
        return
      }

      const parentForNewArm = selectedIsHanded
        ? armReference
        : straightAnchor || normalizedParent || resolveParentForNewArm()
      if (!parentForNewArm) return

      // Unidad de un solo brazo (con mano D/I) que ya tiene su brazo: el
      // retypeExistingArmsTo anterior ya actualizó su tipo. No dibujar otro: un
      // segundo brazo abortaría (lado de mano ocupado) y dispararía el reemplazo
      // global, que recoloca el brazo de forma incorrecta.
      const ownArmsCount = Array.from(useAppStore.getState().items.values()).filter((it: any) => {
        if (!it || it.type !== 'linkedItem') return false
        if (!/^BR\d+(-\d+)?$/i.test(String(it.catalogID || ''))) return false
        return String(it.firstParentID || '') === String(parentForNewArm.id)
      }).length

      if (unitAllowsOnlyOneArm(parentForNewArm) && ownArmsCount > 0) {
        // Ya armada; el tipo se actualizó arriba. Nada más que dibujar.
      } else {
        await drawAndInheritArm(parentForNewArm, item, baseCatalogID, realCatalogID)
      }
      // Solo puede existir un tipo de brazo en escena: unificar también las
      // unidades RELAX con brazo integrado que pudieran estar en otra fila.
      if (clickedBrNumber != null) await retypeRelaxVariantsToBr(clickedBrNumber)
    } catch (error) {
      console.warn('⚠️ [ArmsCushionsPanel] Error al agregar nuevo brazo:', error)
    } finally {
      forceSceneRefresh()
      isProcessingArm.current = false
    }
  }

  // Manejar click en cojín
  const handleCushionClick = async (item: Submenu) => {
    setSelectedCushion(item.claveMenuItem)
    const isRectangularSelection =
      item?.id === 14 ||
      String(item?.claveMenuItem || '').toUpperCase() === '2COJINESRESPALDO' ||
      String(item?.texto || '')
        .toUpperCase()
        .includes('RECTANG')
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        GLOBAL_CUSHION_CATALOG_KEY,
        isRectangularSelection ? 'cojinesR' : 'cojines',
      )
    }

    // Reemplazar cojín en todos los padres reales (sin depender de packGroupId).
    const groups = getSeatParentItems()
      .map((parent: any) => [String(parent.id), parent] as [string, any])
      .filter(([, parent]) => Number((parent?.childs as Set<string> | undefined)?.size || 0) > 0)
    const affectedBaseIds: string[] = []

    for (const [baseId, baseItem] of groups) {
      const baseCatalogID = childBaseCatalogFromUnit(baseItem?.catalogID)
      if (!baseCatalogID) continue
      const targetCatalogID = `${isRectangularSelection ? 'cojinesR' : 'cojines'} ${baseCatalogID}`
      const childIds = Array.from((baseItem.childs as Set<string>) || [])
      const oldCushionId = childIds.find((childId) => {
        const child: any = items.get(childId as any)
        return child?.type === 'linkedSon' && isCushionCatalog(child.catalogID)
      })
      if (!oldCushionId) continue
      const oldCushionItem: any = items.get(oldCushionId as any)
      if (!oldCushionItem?.matrix) continue

      const preservedLocalY =
        Number.isFinite(Number(oldCushionItem.y)) && Math.abs(Number(oldCushionItem.y)) <= 600
          ? Number(oldCushionItem.y)
          : 0
      const newCushionId = await drawItem(
        getId('linkedSon'),
        targetCatalogID,
        item.icono || '',
        true,
        new Matrix4(),
        'linkedSon',
      )
      if (!newCushionId) continue

      editItem(
        newCushionId as any,
        {
          firstParentID: baseId,
          depth: 1,
          x: 0,
          y: preservedLocalY,
          z: 0,
          matrix: new Matrix4().setPosition(0, preservedLocalY, 0),
          isBlocked: true,
        } as any,
      )
      setCollision(false, newCushionId as any)

      const nextChilds = new Set(childIds.filter((id) => id !== oldCushionId))
      nextChilds.add(newCushionId as string)
      editItem(baseId as any, { childs: nextChilds } as any)

      useAppStore.setState((state: any) => {
        state.items.delete(oldCushionId)
        state.layers[state.currentLayer].items.delete(oldCushionId)
        state.layers[state.currentLayer].selected.delete(oldCushionId)
        state.sceneBoundingBoxes.delete(oldCushionId)
        state.collisionableItems.delete(oldCushionId)
      })
      affectedBaseIds.push(baseId as string)
      forceSceneRefresh()
    }
    selectFullGroups(affectedBaseIds)
    forceSceneRefresh()
  }

  const getCushionSubtitle = (item: Submenu) => {
    if (item.id === 14 || item.claveMenuItem === '2COJINESRESPALDO') {
      return ui('rectangularCushions')
    }
    if (item.id === 15 || item.claveMenuItem === '3COJINES60X602COJINES50X50') {
      return ui('multiCushions')
    }
    return ''
  }

  const hasSeatParents = getSeatParentItems().length > 0
  const hasAnyArmInScene = Array.from(items.values()).some((sceneItem: any) => {
    if (!sceneItem || sceneItem.type !== 'linkedItem') return false
    return /^BR\d+(-\d+)?$/i.test(String(sceneItem.catalogID || ''))
  })
  const relaxArmModeParent = resolveParentForNewArm()
  const isRelaxArmModeActive = Boolean(
    relaxArmModeParent && isRelaxIntegratedSceneItem(relaxArmModeParent, relaxBaseCodes),
  )
  if (armsItems.length === 0 && cushionsItems.length === 0 && !hasSeatParents) {
    return null
  }

  const currentItems = activeView === 'arms' ? armsItems : cushionsItems
  // const currentSelected = activeView === 'arms' ? selectedArm : selectedCushion
  const handleItemClick = activeView === 'arms' ? handleArmClick : handleCushionClick
  const isEmbedded = presentation === 'embedded'
  const optionTabClass = isEmbedded
    ? 'mb-0.5 flex h-10 w-full min-w-0 items-center justify-center px-0.5 py-0 text-center text-[13px] font-medium leading-tight transition-all duration-200'
    : 'mb-1 flex h-12 w-full min-w-0 items-center justify-center px-1 py-0.5 text-center text-base leading-tight transition-all duration-200 min-[640px]:h-14 min-[640px]:px-2 min-[640px]:text-base laptop:h-14 laptop:text-base min-[1024px]:text-sm min-[2400px]:h-16 min-[1024px]:whitespace-nowrap min-[2400px]:px-3 min-[2400px]:text-xl'
  const optionTabActive =
    'border-colorAtemporal border-b-2 bg-white text-C/Color3'
  const optionTabIdle = 'border-transparent bg-white text-C/Color3'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      className={
        isEmbedded
          ? 'relative mx-auto h-full w-full max-w-full min-w-0 overflow-y-auto overflow-x-hidden bg-C/Color1 p-2 pt-1'
          : 'border-colorAtemporal relative mx-auto max-h-[min(calc(70dvh-50px),calc(100vh-6rem-50px))] w-fit max-w-full min-w-0 overflow-visible rounded-xl border bg-C/Color1 p-2 pt-1 shadow-lg min-[640px]:p-3 min-[640px]:pt-1.5 min-[1024px]:max-h-[min(calc(65dvh-50px),calc(100vh-7rem-50px))] min-[2400px]:p-5 min-[2400px]:pt-2.5'
      }
    >
      {/* Header con toggle — en embedded (touch) se oculta el badge "2 · Elegir opciones" */}
      <div className='mb-1'>
        {!isEmbedded ? (
          <div className='mx-auto mb-0.5 w-fit rounded-2xl border border-[#D7C9A6]/55 bg-white/95 px-2.5 py-1 shadow-[0_6px_16px_rgba(0,0,0,0.06)] backdrop-blur-[2px]'>
            <div className='flex items-center gap-2.5'>
              <span className='flex h-10 w-10 items-center justify-center rounded-full border border-[#C8A85A] bg-gradient-to-b from-[#E1C57A] to-[#C8A85A] text-xl font-extrabold text-white shadow-[0_3px_8px_rgba(200,168,90,0.4)]'>
                2
              </span>
              <div className='text-left leading-tight'>
                <p className='text-base font-semibold tracking-[0.02em] text-C/Color3'>
                  {ui('chooseOptions')}
                </p>
              </div>
            </div>
          </div>
        ) : null}
        {/* Botones de toggle */}
        <div
          className={
            isEmbedded
              ? 'grid grid-cols-5 gap-0.5'
              : 'grid grid-cols-5 gap-1 min-[640px]:gap-2 min-[2400px]:gap-3'
          }
        >
          <motion.button
            onClick={() => setActiveView('comfort')}
            className={`${optionTabClass} ${
              activeView === 'comfort' ? optionTabActive : optionTabIdle
            }`}
            whileHover={isEmbedded ? undefined : { scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {ui('depth')}
          </motion.button>

          <motion.button
            onClick={() => setActiveView('arms')}
            className={`${optionTabClass} ${
              activeView === 'arms' ? optionTabActive : optionTabIdle
            }`}
            whileHover={isEmbedded ? undefined : { scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isEmbedded ? (
              ui('armTypeEmbedded').split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))
            ) : (
              <>
                <span className='min-[1024px]:hidden'>
                  {ui('armTypeShort').split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </span>
                <span className='hidden min-[1024px]:inline'>{ui('armType')}</span>
              </>
            )}
          </motion.button>

          <motion.button
            onClick={() => setActiveView('cushions')}
            className={`${optionTabClass} ${
              activeView === 'cushions' ? optionTabActive : optionTabIdle
            }`}
            whileHover={isEmbedded ? undefined : { scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isEmbedded ? (
              ui('cushionTypeEmbedded').split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))
            ) : (
              <>
                <span className='min-[1024px]:hidden'>
                  {ui('cushionTypeShort').split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </span>
                <span className='hidden min-[1024px]:inline'>{ui('cushionType')}</span>
              </>
            )}
          </motion.button>

          <motion.button
            onClick={() => setActiveView('seat')}
            className={`relative z-10 ${optionTabClass} ${
              activeView === 'seat' ? optionTabActive : optionTabIdle
            }`}
            whileHover={isEmbedded ? undefined : { scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isEmbedded ? (
              ui('seatStitchTabEmbedded').split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))
            ) : (
              <>
                <span className='min-[1024px]:hidden'>
                  {ui('seatStitchTabShort').split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </span>
                <span className='hidden min-[1024px]:inline'>{ui('seatStitchTab')}</span>
              </>
            )}
          </motion.button>

          <motion.button
            onClick={() => setActiveView('seatHeight')}
            className={`${optionTabClass} ${
              activeView === 'seatHeight' ? optionTabActive : optionTabIdle
            }`}
            whileHover={isEmbedded ? undefined : { scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isEmbedded ? (
              ui('seatHeightTabEmbedded').split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 ? <br /> : null}
                </React.Fragment>
              ))
            ) : (
              ui('seatHeightTab')
            )}
          </motion.button>
        </div>
      </div>
      <div className='max-h-[calc(12.5rem-50px)] min-h-[calc(10rem-50px)] overflow-y-auto overflow-x-hidden overscroll-contain min-[640px]:max-h-[calc(13.5rem-50px)] laptop:max-h-[calc(15rem-50px)] min-[2400px]:max-h-[calc(24rem-50px)] min-[2400px]:min-h-[calc(15rem-50px)]'>
        {activeView === 'comfort' ? (
          <div className='mt-1 grid min-h-20 grid-cols-4 gap-1 min-[640px]:gap-2 laptop:gap-2 min-[2400px]:gap-4'>
            {COMFORT_OPTIONS.map((option) => {
              const isSelected = canApplyComfort && selectedComfort === option.id
              const isDisabled = !canApplyComfort
              return (
                <div
                  key={option.id}
                  className='flex flex-col items-center'
                >
                  <motion.div
                    onClick={isDisabled ? undefined : () => handleComfortClick(option.id)}
                    className={`
                      ${PORTRAIT_OPTION_TILE_CLASS}
                      ${
                        isDisabled
                          ? 'border-colorAtemporal/15 cursor-not-allowed opacity-40 grayscale'
                          : isSelected
                            ? 'border-colorAtemporal bg-colorAtemporal shadow-md'
                            : 'border-colorAtemporal/30 hover:border-colorAtemporal/60 hover:bg-C/Color2'
                      }
                    `}
                    whileHover={isDisabled ? undefined : { scale: 1.05 }}
                    whileTap={isDisabled ? undefined : { scale: 0.95 }}
                    role={isDisabled ? 'presentation' : 'button'}
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                    onKeyDown={
                      isDisabled
                        ? undefined
                        : (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleComfortClick(option.id)
                            }
                          }
                    }
                  >
                    <div className='relative z-0 flex h-full min-h-0 w-full items-center justify-center p-0.5'>
                      {isDisabled ? (
                        <img
                          src={COMFORT_PREVIEW_SRC[option.id]}
                          alt={option.label}
                          className='max-h-full max-w-full object-contain'
                          draggable={false}
                        />
                      ) : isSelected ? (
                        <img
                          src={COMFORT_PREVIEW_SRC[option.id]}
                          alt={option.label}
                          className='max-h-full max-w-full object-contain'
                        />
                      ) : (
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <img
                                src={COMFORT_PREVIEW_SRC[option.id]}
                                alt={option.label}
                                className='max-h-full max-w-full object-contain'
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side='top'
                                align='center'
                                className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                              >
                                {ui('replaceByDepth')} {option.label}
                                <Tooltip.Arrow className='fill-C/Color1' />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      )}
                    </div>
                    {isSelected && (
                      <div className='absolute bottom-1 left-1 h-2 w-2 rounded-full bg-C/Color3' />
                    )}
                  </motion.div>
                  <div
                    className={`mt-1 text-center text-sm font-medium leading-tight text-C/Color3 min-[640px]:text-base min-[2400px]:text-xl ${
                      isDisabled ? 'opacity-40' : ''
                    }`}
                  >
                    {option.label}
                  </div>
                </div>
              )
            })}
          </div>
        ) : activeView === 'seatHeight' ? (
          <div className='grid min-h-20 grid-cols-2 gap-3 pt-3 laptop:gap-4 min-[2400px]:gap-10 min-[2400px]:pt-6'>
            <div className='flex flex-col items-center'>
              <motion.div
                onClick={() => applySeatHeightSelection('47')}
                className={`
                ${SQUARE_PREVIEW_TILE_CLASS}
                ${
                  selectedSeatHeightCm === '47'
                    ? 'border-colorAtemporal bg-colorAtemporal shadow-md'
                    : 'border-colorAtemporal/30 hover:border-colorAtemporal/60 hover:bg-C/Color2'
                }
              `}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    applySeatHeightSelection('47')
                  }
                }}
              >
                <div className='flex h-full w-full items-center justify-center p-0.5 min-[2400px]:p-0.5'>
                  {selectedSeatHeightCm === '47' ? (
                    <img
                      src={seatHeightPreview47}
                      alt={`${ui('seatHeightAlt')} 47`}
                      className='h-full w-full object-contain'
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <img
                            src={seatHeightPreview47}
                            alt={`${ui('seatHeightAlt')} 47`}
                            className='h-full w-full object-contain'
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side='top'
                            align='center'
                            className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                          >
                            {ui('replaceByHeight')}
                            <Tooltip.Arrow className='fill-C/Color1' />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  )}
                </div>
                {selectedSeatHeightCm === '47' && (
                  <div className='absolute right-1 top-1 h-2 w-2 rounded-full bg-C/Color3' />
                )}
              </motion.div>
              <div className='mt-1 text-center text-sm font-medium leading-tight text-C/Color3 min-[640px]:text-base min-[2400px]:text-xl'>
                47cm
              </div>
            </div>
            <div className='flex flex-col items-center'>
              <motion.div
                onClick={() => applySeatHeightSelection('42')}
                className={`
                ${SQUARE_PREVIEW_TILE_CLASS}
                ${
                  selectedSeatHeightCm === '42'
                    ? 'border-colorAtemporal bg-colorAtemporal shadow-md'
                    : 'border-colorAtemporal/30 hover:border-colorAtemporal/60 hover:bg-C/Color2'
                }
              `}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    applySeatHeightSelection('42')
                  }
                }}
              >
                <div className='flex h-full w-full items-center justify-center p-0.5 min-[2400px]:p-0.5'>
                  {selectedSeatHeightCm === '42' ? (
                    <img
                      src={seatHeightPreview42}
                      alt={`${ui('seatHeightAlt')} 42`}
                      className='h-full w-full object-contain'
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <img
                            src={seatHeightPreview42}
                            alt={`${ui('seatHeightAlt')} 42`}
                            className='h-full w-full object-contain'
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side='top'
                            align='center'
                            className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                          >
                            {ui('replaceByHeight')}
                            <Tooltip.Arrow className='fill-C/Color1' />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  )}
                </div>
                {selectedSeatHeightCm === '42' && (
                  <div className='absolute right-1 top-1 h-2 w-2 rounded-full bg-C/Color3' />
                )}
              </motion.div>
              <div className='mt-1 text-center text-sm font-medium leading-tight text-C/Color3 min-[640px]:text-base min-[2400px]:text-xl'>
                42cm
              </div>
            </div>
          </div>
        ) : activeView === 'seat' ? (
          <div className='grid min-h-20 grid-cols-2 gap-3 pt-3 laptop:gap-4 min-[2400px]:gap-10 min-[2400px]:pt-6'>
            <div className='flex flex-col items-center'>
              <motion.div
                onClick={() => applySeatStitchSelection('sin')}
                className={`
                ${SQUARE_PREVIEW_TILE_CLASS}
                ${
                  selectedSeatStitch === 'sin'
                    ? 'border-colorAtemporal bg-colorAtemporal shadow-md'
                    : 'border-colorAtemporal/30 hover:border-colorAtemporal/60 hover:bg-C/Color2'
                }
              `}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    applySeatStitchSelection('sin')
                  }
                }}
              >
                <div className='flex h-full w-full items-center justify-center p-0.5 min-[2400px]:p-0.5'>
                  {selectedSeatStitch === 'sin' ? (
                    <img
                      src={seatStitchSinPreview}
                      alt={ui('withoutStitch')}
                      className='h-full w-full object-contain'
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <img
                            src={seatStitchSinPreview}
                            alt={ui('withoutStitch')}
                            className='h-full w-full object-contain'
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side='top'
                            align='center'
                            className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                          >
                            {ui('replaceBySeatModel')}
                            <Tooltip.Arrow className='fill-C/Color1' />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  )}
                </div>
                {selectedSeatStitch === 'sin' && (
                  <div className='absolute right-1 top-1 h-2 w-2 rounded-full bg-C/Color3' />
                )}
              </motion.div>
              <div className='mt-1 text-center text-sm font-medium leading-tight text-C/Color3 min-[640px]:text-base min-[2400px]:text-xl'>
                {ui('withoutStitchLabel')}
              </div>
            </div>
            <div className='flex flex-col items-center'>
              <motion.div
                onClick={() => applySeatStitchSelection('con')}
                className={`
                ${SQUARE_PREVIEW_TILE_CLASS}
                ${
                  selectedSeatStitch === 'con'
                    ? 'border-colorAtemporal bg-colorAtemporal shadow-md'
                    : 'border-colorAtemporal/30 hover:border-colorAtemporal/60 hover:bg-C/Color2'
                }
              `}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    applySeatStitchSelection('con')
                  }
                }}
              >
                <div className='flex h-full w-full items-center justify-center p-0.5 min-[2400px]:p-0.5'>
                  {selectedSeatStitch === 'con' ? (
                    <img
                      src={seatStitchConPreview}
                      alt={ui('withStitch')}
                      className='h-full w-full object-contain'
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <Tooltip.Provider>
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <img
                            src={seatStitchConPreview}
                            alt={ui('withStitch')}
                            className='h-full w-full object-contain'
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                          <Tooltip.Content
                            side='top'
                            align='center'
                            className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                          >
                            {ui('replaceBySeatModel')}
                            <Tooltip.Arrow className='fill-C/Color1' />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </Tooltip.Provider>
                  )}
                </div>
                {selectedSeatStitch === 'con' && (
                  <div className='absolute right-1 top-1 h-2 w-2 rounded-full bg-C/Color3' />
                )}
              </motion.div>
              <div className='mt-1 text-center text-sm font-medium leading-tight text-C/Color3 min-[640px]:text-base min-[2400px]:text-xl'>
                {ui('withStitchLabel')}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={
              activeView === 'arms'
                ? 'mt-2 grid min-h-20 grid-cols-5 gap-1 min-[640px]:gap-2 laptop:gap-2 min-[2400px]:gap-4'
                : activeView === 'cushions'
                  ? 'grid min-h-20 grid-cols-2 gap-3 pt-3 laptop:gap-4 min-[2400px]:gap-10 min-[2400px]:pt-6'
                  : 'mx-auto mt-2 grid min-h-20 w-fit grid-cols-2 justify-items-center gap-3 pt-3 laptop:gap-4 min-[2400px]:gap-10 min-[2400px]:pt-6'
            }
          >
            {currentItems.map((item: any) => {
              const isSelected = isItemSelected(item.claveMenuItem)
              const cushionSubtitle = activeView === 'cushions' ? getCushionSubtitle(item) : ''
              const displayLabel =
                activeView === 'arms'
                  ? String(item?.texto || '')
                      .trim()
                      .replace(/^BR(\d+)$/i, 'B$1')
                  : String(item?.texto || '')

              return (
                <div
                  key={item.claveMenuItem}
                  className={
                    activeView === 'cushions' || activeView === 'arms'
                      ? 'flex flex-col items-center'
                      : ''
                  }
                >
                  <motion.div
                    onClick={() => handleItemClick(item)}
                    className={`
                  ${
                    activeView === 'cushions'
                      ? SQUARE_PREVIEW_TILE_CLASS
                      : activeView === 'arms'
                        ? PORTRAIT_OPTION_TILE_CLASS
                        : 'relative aspect-square w-[clamp(3.25rem,18%,4.75rem)] cursor-pointer rounded border-2 transition-all duration-200 min-[640px]:w-20 laptop:w-20 min-[2400px]:w-28'
                  }
                  ${
                    isSelected
                      ? 'border-colorAtemporal bg-colorAtemporal px-0.5 shadow-md'
                      : 'border-colorAtemporal/30 hover:border-colorAtemporal/60 hover:bg-C/Color2'
                  }
                `}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleItemClick(item)
                      }
                    }}
                  >
                    {/* Imagen del item (debajo del botón en capa; el botón va después en el DOM para quedar encima) */}
                    <div
                      className={`relative z-0 w-full ${
                        activeView === 'cushions'
                          ? 'flex h-full w-full items-center justify-center p-0.5 min-[2400px]:p-0.5'
                          : activeView === 'arms'
                            ? 'flex h-full min-h-0 w-full items-center justify-center p-0.5'
                            : 'h-full p-0.5'
                      }`}
                    >
                      {activeView === 'cushions' ? (
                        isSelected ? (
                          <img
                            src={getCushionPreviewSrc(item) ?? item.icono ?? DEFAULT_CUSHION_IMAGE}
                            alt={displayLabel}
                            className='h-full w-full object-contain'
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                            }}
                          />
                        ) : (
                          <Tooltip.Provider>
                            <Tooltip.Root>
                              <Tooltip.Trigger asChild>
                                <img
                                  src={
                                    getCushionPreviewSrc(item) ??
                                    item.icono ??
                                    DEFAULT_CUSHION_IMAGE
                                  }
                                  alt={displayLabel}
                                  className='h-full w-full object-contain'
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.style.display = 'none'
                                  }}
                                />
                              </Tooltip.Trigger>
                              <Tooltip.Portal>
                                <Tooltip.Content
                                  side='top'
                                  align='center'
                                  className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                                >
                                  {ui('replaceByCushionModel')}
                                  <Tooltip.Arrow className='fill-C/Color1' />
                                </Tooltip.Content>
                              </Tooltip.Portal>
                            </Tooltip.Root>
                          </Tooltip.Provider>
                        )
                      ) : activeView === 'arms' ? (
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <img
                                src={
                                  getArmPreviewSrc(item.claveMenuItem) ?? TEMP_ARM_PLACEHOLDER_IMAGE
                                }
                                alt={displayLabel}
                                className='max-h-full max-w-full object-contain'
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.style.display = 'none'
                                }}
                              />
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                side='top'
                                align='center'
                                className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                              >
                                {isRelaxArmModeActive
                                  ? ui('replaceArmVariant')
                                  : ui('addNewArm')}
                                <Tooltip.Arrow className='fill-C/Color1' />
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      ) : item.icono &&
                        item.icono !==
                          'https://atemporalinternalapi.for2home.com/CatalogData/ItemImages/MainMenu/nada.png' ? (
                        <img
                          src={item.icono}
                          alt={displayLabel}
                          className='h-full w-full object-contain'
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            const parent = target.parentElement
                            if (parent) {
                              parent.innerHTML = `<div class="w-full h-full flex items-center justify-center text-C/Color3 text-xs">${displayLabel}</div>`
                            }
                          }}
                        />
                      ) : (
                        <div className='flex h-full w-full items-center justify-center text-sm text-C/Color3'>
                          {displayLabel}
                        </div>
                      )}
                    </div>

                    {activeView === 'arms' && hasAnyArmInScene && !isRelaxArmModeActive && (
                      <Tooltip.Provider>
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <button
                              type='button'
                              onClick={(e) => handleReplaceItem(e, item)}
                              className='border-C/Color3/20 absolute -right-1.5 -top-2 z-30 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border bg-white shadow-md transition-all hover:scale-105 hover:bg-gray-50 hover:shadow-lg min-[2400px]:h-11 min-[2400px]:w-11'
                            >
                              <img
                                src={actualizarImg}
                                alt={ui('replace')}
                                className='pointer-events-none h-4 w-4 select-none object-contain min-[2400px]:h-6 min-[2400px]:w-6'
                                draggable={false}
                              />
                            </button>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              side='top'
                              align='center'
                              className='z-[999999] rounded bg-C/Color1 p-2 text-base font-normal leading-4 text-black shadow-lg outline outline-[0.5px] outline-C/Color3'
                            >
                              {ui('replaceArmsWithThis')}
                              <Tooltip.Arrow className='fill-C/Color1' />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </Tooltip.Provider>
                    )}

                    {/* Indicador de selección */}
                    {isSelected && (
                      <div
                        className={`absolute h-2 w-2 rounded-full bg-C/Color3 ${
                          activeView === 'cushions' ? 'right-1 top-1' : 'bottom-1 left-1'
                        }`}
                      />
                    )}
                  </motion.div>
                  {activeView === 'cushions' && cushionSubtitle && (
                    <div className='mt-1 text-center text-sm font-medium leading-tight text-C/Color3 min-[640px]:text-base min-[2400px]:text-xl'>
                      {cushionSubtitle}
                    </div>
                  )}
                  {activeView === 'arms' && (
                    <div className='mt-0.5 text-center text-xs font-medium leading-tight text-C/Color3 min-[640px]:text-sm min-[2400px]:text-lg'>
                      {displayLabel}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </motion.div>
  )
}
